import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { settingsApi } from '../api';
import PageHeader from '../components/PageHeader';
import { Loader2, Eye, Send, X } from 'lucide-react';

const TabButton = ({ active, onClick, children, primary }) => (
  <button onClick={onClick} className={`px-4 py-1.5 rounded-md text-[13px] font-medium transition-colors ${active ? 'text-white' : 'text-[#232323] hover:bg-[#F1EBDD]'}`}
    style={active ? { background: primary } : {}}>{children}</button>
);

export default function Automailer() {
  const { theme, user } = useAuth();
  const [tab, setTab] = useState('settings');
  const [config, setConfig] = useState(null);
  const [log, setLog] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  // iter44 — Preview / Test-send state (shared across the two template cards)
  const [preview, setPreview] = useState(null); // { template, subject, body }

  useEffect(() => {
    settingsApi.getAutomailer().then(setConfig);
    settingsApi.mailLog().then(setLog).catch(() => {});
  }, []);

  const update = (path, value) => {
    setConfig(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let ptr = next;
      for (let i = 0; i < keys.length - 1; i++) ptr = ptr[keys[i]];
      ptr[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const payload = {
        enabled: config.enabled,
        attachExcelOnNewRequisition: config.attachExcelOnNewRequisition ?? true,
        newRequisition: config.newRequisition,
        dispatch: config.dispatch,
      };
      await settingsApi.setAutomailer(payload);
      setMsg('Saved');
      setTimeout(() => setMsg(''), 2500);
    } catch (e) { alert(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (!config) return <div className="p-10 text-center text-[#7a6f5c]"><Loader2 className="animate-spin inline" /></div>;

  return (
    <div>
      <PageHeader title="Automailer" subtitle="Configure who's notified when a requisition is raised or dispatched" />

      <div className="flex items-center gap-2 mb-5">
        <TabButton active={tab==='settings'} onClick={() => setTab('settings')} primary={theme.primary}>Settings</TabButton>
        <TabButton active={tab==='log'} onClick={() => setTab('log')} primary={theme.primary}>Mail log ({log.length})</TabButton>
      </div>

      {tab === 'settings' && (
        <div className="space-y-4">
          <div className="fm-card p-5">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={config.enabled} onChange={e => update('enabled', e.target.checked)} className="mt-1 w-4 h-4 accent-[#9C3D54]" />
              <span>
                <span className="font-semibold text-[14px]">Automailer enabled</span>
                <div className="text-[12.5px] text-[#7a6f5c] mt-0.5">Master switch. Turn this off to pause every automated email below without losing your templates.</div>
              </span>
            </label>
          </div>

          <MailTemplateCard
            title="Email on new requisition"
            templateKey="newRequisition"
            description="Sent the moment an EBO raises a requisition, with the requisition Excel attached. Placeholders: {requisitionId} {eboName} {eboBranchCode} {requestedBy} {requestedDate} {skuCount}"
            enabled={config.newRequisition.enabled}
            onEnabled={(v) => update('newRequisition.enabled', v)}
            data={config.newRequisition}
            onChange={(k, v) => update(`newRequisition.${k}`, v)}
            onPreview={setPreview}
            testTo={user?.email}
            theme={theme}
            showTo
          />

          <div className="fm-card p-4 flex items-start gap-3">
            <input
              type="checkbox"
              checked={config.attachExcelOnNewRequisition ?? true}
              onChange={e => update('attachExcelOnNewRequisition', e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#9C3D54]"
              id="attach-xlsx"
            />
            <label htmlFor="attach-xlsx" className="cursor-pointer">
              <div className="font-semibold text-[13.5px]">Attach requisition Excel to the new-requisition email</div>
              <div className="text-[12px] text-[#7a6f5c] mt-0.5">When on, the warehouse (and CC'd EBO manager) receive the fully-formatted requisition as an <span className="font-mono">.xlsx</span> attachment for pick / dispatch — no manual export needed.</div>
            </label>
          </div>

          <MailTemplateCard
            title="Email on dispatch"
            templateKey="dispatch"
            description="Sent to the requesting EBO branch the moment a SKU is marked shipped. Placeholders: {requisitionId} {skuName} {skuCode} {qty} {courierName} {trackingNumber}"
            enabled={config.dispatch.enabled}
            onEnabled={(v) => update('dispatch.enabled', v)}
            data={config.dispatch}
            onChange={(k, v) => update(`dispatch.${k}`, v)}
            onPreview={setPreview}
            testTo={user?.email}
            theme={theme}
            ccLabel="Cc (optional, comma-separated) — the EBO branch itself is always the 'To'"
          />

          <div className="flex items-center gap-3">
            <button data-testid="automailer-save-btn" disabled={saving} onClick={save} className="px-4 py-2 rounded-md text-white text-[14px] font-medium disabled:opacity-60" style={{ background: theme.primary }}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            {msg && <span className="text-[13px] text-[#2f6b41]">{msg}</span>}
          </div>
        </div>
      )}

      {tab === 'log' && (
        log.length === 0 ? (
          <div className="fm-card p-8 text-center text-[13px] text-[#7a6f5c]">No automated emails have been sent yet.</div>
        ) : (
          <div className="fm-card overflow-hidden">
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="text-left text-[12px] text-[#7a6f5c] border-b border-[#E7E2D3]">
                  <th className="px-5 py-3 font-medium">Sent at (UTC)</th>
                  <th className="px-5 py-3 font-medium">To</th>
                  <th className="px-5 py-3 font-medium">Subject</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {log.map((m, i) => (
                  <tr key={i} className="border-t border-[#EFEADC]">
                    <td className="px-5 py-2 font-mono text-[12px]">{new Date(m.sentAt).toLocaleString()}</td>
                    <td className="px-5 py-2">{m.to}</td>
                    <td className="px-5 py-2">{m.subject}</td>
                    <td className="px-5 py-2">{m.success ? <span className="text-[#2f6b41]">Sent</span> : <span className="text-[#a04747]">{m.error}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      <PreviewModal payload={preview} onClose={() => setPreview(null)} />
    </div>
  );
}

function MailTemplateCard({ title, description, enabled, onEnabled, data, onChange, showTo, ccLabel, templateKey, onPreview, testTo, theme }) {
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  const currentSubject = data.subject || '';
  const currentBody = data.body || '';
  const setSubject = (val) => onChange('subject', val);
  const setBody = (val) => onChange('body', val);

  const runPreview = async () => {
    setBusy('preview'); setErr(''); setNote('');
    try {
      const r = await settingsApi.previewAutomailer({
        template: templateKey,
        subject: currentSubject,
        body: currentBody,
      });
      onPreview?.({ template: r.template, subject: r.subject, body: r.body, title });
    } catch (e) {
      setErr(e.response?.data?.detail || 'Preview failed');
    } finally { setBusy(''); }
  };

  const runTestSend = async () => {
    if (!testTo) { setErr('Your account has no email address on file'); return; }
    if (!window.confirm(`Send a test copy to ${testTo}?`)) return;
    setBusy('send'); setErr(''); setNote('');
    try {
      const r = await settingsApi.testSendAutomailer({
        template: templateKey,
        subject: currentSubject,
        body: currentBody,
      });
      setNote(`Test sent to ${r.sent_to}.`);
      setTimeout(() => setNote(''), 6000);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Test send failed');
    } finally { setBusy(''); }
  };

  return (
    <div className="fm-card p-5" data-testid={`mail-tpl-card-${templateKey}`}>
      <label className="flex items-start gap-3 cursor-pointer mb-3">
        <input type="checkbox" checked={enabled} onChange={e => onEnabled(e.target.checked)} className="mt-1 w-4 h-4 accent-[#9C3D54]" />
        <span>
          <span className="font-semibold text-[14px]">{title}</span>
          <div className="text-[12.5px] text-[#7a6f5c] mt-0.5 leading-relaxed max-w-[900px]">{description}</div>
        </span>
      </label>

      <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${enabled ? '' : 'opacity-50 pointer-events-none'}`}>
        {showTo && (
          <div>
            <label className="block text-[12.5px] text-[#232323] mb-1 font-medium">To</label>
            <input value={data.to || ''} onChange={e => onChange('to', e.target.value)} className="fm-focus w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white" />
          </div>
        )}
        <div className={showTo ? '' : 'md:col-span-2'}>
          <label className="block text-[12.5px] text-[#232323] mb-1 font-medium">{ccLabel || 'Cc (optional, comma-separated)'}</label>
          <input value={data.cc || ''} onChange={e => onChange('cc', e.target.value)} className="fm-focus w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-[12.5px] text-[#232323] mb-1 font-medium">
            Subject
          </label>
          <input value={currentSubject} onChange={e => setSubject(e.target.value)} data-testid={`mail-tpl-subject-${templateKey}`} className="fm-focus w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-[12.5px] text-[#232323] mb-1 font-medium">
            Body
          </label>
          <textarea value={currentBody} onChange={e => setBody(e.target.value)} data-testid={`mail-tpl-body-${templateKey}`} rows={5} className="fm-focus w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white leading-relaxed" />
        </div>
      </div>
      {/* iter44 — Preview + Send-test toolbar */}
      <div className={`mt-3 flex flex-wrap items-center gap-2 ${enabled ? '' : 'opacity-50 pointer-events-none'}`}>
        <button
          onClick={runPreview}
          disabled={!!busy}
          data-testid={`mail-tpl-preview-${templateKey}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#E7E2D3] bg-white text-[12.5px] hover:bg-[#F7F4EA] disabled:opacity-60"
        >
          {busy === 'preview' ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />}
          Preview
        </button>
        <button
          onClick={runTestSend}
          disabled={!!busy || !testTo}
          data-testid={`mail-tpl-testsend-${templateKey}`}
          title={testTo ? `Send a test copy to ${testTo}` : 'No email on file for your account'}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-white text-[12.5px] disabled:opacity-60"
          style={{ background: theme?.primary || '#9C3D54' }}
        >
          {busy === 'send' ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
          Send test to me{testTo ? ` (${testTo})` : ''}
        </button>
        {note && <span className="text-[12px] text-[#2f6b41]" data-testid={`mail-tpl-note-${templateKey}`}>{note}</span>}
        {err && <span className="text-[12px] text-[#B84A4A]" data-testid={`mail-tpl-err-${templateKey}`}>{err}</span>}
      </div>
    </div>
  );
}

/** iter44 — Full-screen preview overlay that renders the substituted subject +
 *  body using sample data. Read-only. */
function PreviewModal({ payload, onClose }) {
  if (!payload) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      data-testid="mail-preview-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="fm-card w-full max-w-[700px] bg-white overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E7E2D3]">
          <div className="flex items-center gap-2 text-[15px] font-semibold">
            <Eye size={16} /> Preview · <span className="text-[#7a6f5c]">{payload.title}</span>
          </div>
          <button onClick={onClose} className="text-[#7a6f5c] hover:text-[#232323]" data-testid="mail-preview-close">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 max-h-[70vh] overflow-y-auto">
          <div className="text-[11px] uppercase tracking-wide text-[#7a6f5c] mb-1">Subject</div>
          <div className="fm-card p-3 text-[13.5px] font-medium mb-4" data-testid="mail-preview-subject">{payload.subject || <span className="text-[#B84A4A]">(empty)</span>}</div>
          <div className="text-[11px] uppercase tracking-wide text-[#7a6f5c] mb-1">Body</div>
          <div
            className="fm-card p-3 text-[13.5px] whitespace-pre-wrap leading-relaxed"
            data-testid="mail-preview-body"
          >{payload.body || <span className="text-[#B84A4A]">(empty)</span>}</div>
          <div className="mt-3 text-[11.5px] text-[#7a6f5c] italic">
            Sample data is used for the placeholders. Real emails will use the actual requisition values.
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[#E7E2D3] bg-[#FAF7EE]">
          <button
            onClick={onClose}
            data-testid="mail-preview-done"
            className="px-4 py-1.5 rounded-md text-[13px] text-white font-medium"
            style={{ background: '#232323' }}
          >Close</button>
        </div>
      </div>
    </div>
  );
}
